import { useCallback, useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, MessageSquare, RefreshCw, Send, XCircle } from 'lucide-react';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import InlineAlert from '../../components/common/InlineAlert';
import { ACCOUNT_SUSPENDED_MESSAGE_KEY, apiErrorInfo, isAccountSuspended } from '../../utils/apiErrorInfo';
import {
  closeAdvertisement,
  replyToAdvertisement,
  type Advertisement,
} from '../../services/advertisementService';
import { boardIssueOf, getRequest, type BoardIssue } from './boardApi';

/**
 * One request on the board: a disclosure (APG accordion: heading > button with
 * aria-expanded) that opens onto the full text, the replies, a reply field and,
 * for the person who posted it, a close action behind an inline confirmation.
 *
 * The card loads its own replies when opened. Failures that end the whole board
 * (signed out, not professional) go up through `onAccessIssue`; the rest stay on
 * the card.
 */
export interface AdItemProps {
  ad: Advertisement;
  token: string | null;
  currentUserId: string | null;
  open: boolean;
  onToggle: () => void;
  onAccessIssue: (issue: BoardIssue) => void;
  /** A reply was posted: the page bumps the count on the card. */
  onReplied: (id: string) => void;
  /** The owner closed the request: the page drops it from the open list. */
  onClosed: (id: string) => void;
}

type DetailState = 'idle' | 'loading' | 'ready' | 'error';

export default function AdItem({
  ad,
  token,
  currentUserId,
  open,
  onToggle,
  onAccessIssue,
  onReplied,
  onClosed,
}: AdItemProps) {
  const { t } = useTranslation(['ads', 'common']);
  const uid = useId();
  const ids = {
    toggle: `${uid}-toggle`,
    title: `${uid}-title`,
    excerpt: `${uid}-excerpt`,
    meta: `${uid}-meta`,
    panel: `${uid}-panel`,
    reply: `${uid}-reply`,
    replyError: `${uid}-reply-error`,
    confirm: `${uid}-confirm`,
  };

  const [detail, setDetail] = useState<Advertisement | null>(null);
  const [detailState, setDetailState] = useState<DetailState>('idle');
  const [reply, setReply] = useState('');
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);

  const closeTriggerRef = useRef<HTMLButtonElement>(null);
  const confirmTextRef = useRef<HTMLParagraphElement>(null);
  const pendingFocus = useRef<'confirm' | 'trigger' | null>(null);
  const detailSeq = useRef(0);

  // The latest callback without re-creating loadDetail (and re-fetching) whenever
  // the page re-renders with a new function.
  const onAccessIssueRef = useRef(onAccessIssue);
  useEffect(() => {
    onAccessIssueRef.current = onAccessIssue;
  });

  const isOwner = currentUserId !== null && ad.userId === currentUserId;
  const replyCount = ad._count?.replies ?? 0;

  const loadDetail = useCallback(async () => {
    if (!token) return;
    // Only the newest request may write (open, close and reopen can overlap).
    const seq = ++detailSeq.current;
    setDetailState('loading');
    try {
      const next = await getRequest(token, ad.id);
      if (seq !== detailSeq.current) return;
      setDetail(next);
      setDetailState(next ? 'ready' : 'error');
    } catch (error) {
      if (seq !== detailSeq.current) return;
      const issue = boardIssueOf(error);
      if (issue) {
        onAccessIssueRef.current(issue);
        return;
      }
      setDetailState('error');
    }
  }, [token, ad.id]);

  useEffect(() => {
    if (open) {
      void loadDetail();
    } else {
      setConfirmingClose(false);
      setCloseError(null);
      setReplyError(null);
    }
  }, [open, loadDetail]);

  useEffect(() => {
    if (pendingFocus.current === 'confirm') confirmTextRef.current?.focus();
    if (pendingFocus.current === 'trigger') closeTriggerRef.current?.focus();
    pendingFocus.current = null;
  }, [confirmingClose]);

  const submitReply = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const message = reply.trim();
    if (!token || !message || replyBusy) return;
    setReplyBusy(true);
    setReplyError(null);
    try {
      await replyToAdvertisement(token, ad.id, message);
      setReply('');
      onReplied(ad.id);
      void loadDetail();
    } catch (error) {
      const issue = boardIssueOf(error);
      if (issue) {
        onAccessIssueRef.current(issue);
        return;
      }
      const { status } = apiErrorInfo(error);
      setReplyError(
        isAccountSuspended(error)
          ? ACCOUNT_SUSPENDED_MESSAGE_KEY
          : status === 409
            ? 'errors.replyClosed'
            : status === 404
              ? 'errors.notFound'
              : 'errors.reply'
      );
    } finally {
      setReplyBusy(false);
    }
  };

  const startClose = () => {
    setCloseError(null);
    pendingFocus.current = 'confirm';
    setConfirmingClose(true);
  };

  const cancelClose = () => {
    pendingFocus.current = 'trigger';
    setConfirmingClose(false);
  };

  const confirmClose = async () => {
    if (!token || closeBusy) return;
    setCloseBusy(true);
    setCloseError(null);
    try {
      await closeAdvertisement(token, ad.id);
      onClosed(ad.id);
    } catch (error) {
      const issue = boardIssueOf(error);
      if (issue) {
        onAccessIssueRef.current(issue);
        return;
      }
      const { status } = apiErrorInfo(error);
      setCloseError(
        isAccountSuspended(error)
          ? ACCOUNT_SUSPENDED_MESSAGE_KEY
          : status === 403
            ? 'errors.notOwner'
            : status === 404
              ? 'errors.notFound'
              : 'errors.close'
      );
    } finally {
      setCloseBusy(false);
    }
  };

  const replies = detail?.replies ?? [];

  return (
    <li>
      <article
        aria-labelledby={ids.title}
        className="overflow-hidden rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] shadow-brand-xs"
      >
        <h3 className="m-0">
          <button
            id={ids.toggle}
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={ids.panel}
            aria-labelledby={ids.title}
            aria-describedby={open ? ids.meta : `${ids.excerpt} ${ids.meta}`}
            className="flex w-full items-start gap-3 px-4 py-4 text-start transition-colors duration-200 hover:bg-[var(--pv-color-surface-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#004D40]/20 sm:px-5"
          >
            <span className="min-w-0 flex-1">
              <span
                id={ids.title}
                className="block text-base font-bold leading-snug text-[var(--pv-color-text)] [overflow-wrap:anywhere]"
              >
                {ad.title}
              </span>
              {!open ? (
                <span
                  id={ids.excerpt}
                  className="mt-1 line-clamp-2 block text-[0.9375rem] leading-relaxed text-[var(--pv-color-text-muted)] [overflow-wrap:anywhere]"
                >
                  {ad.body}
                </span>
              ) : null}
              <span id={ids.meta} className="mt-2 flex flex-wrap items-center gap-2 text-[0.875rem] text-[var(--pv-color-text-muted)]">
                {ad.category ? (
                  <span className="inline-flex items-center rounded-full bg-[var(--pv-color-surface-sunken)] px-2.5 py-0.5 text-[var(--pv-color-primary)] ring-1 ring-inset ring-[var(--pv-border-soft)] [overflow-wrap:anywhere]">
                    {ad.category}
                  </span>
                ) : null}
                {isOwner ? (
                  <span className="inline-flex items-center rounded-full border border-[var(--pv-border-soft)] px-2.5 py-0.5 text-[var(--pv-color-text)]">
                    {t('list.yours')}
                  </span>
                ) : null}
                <span className="inline-flex items-center gap-1">
                  <MessageSquare className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {t('list.replies', { n: replyCount })}
                </span>
              </span>
            </span>
            <ChevronDown
              className={`mt-0.5 h-5 w-5 shrink-0 text-[var(--pv-color-text-muted)] transition-transform duration-200 motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        </h3>

        {open ? (
          <div
            id={ids.panel}
            role="region"
            aria-labelledby={ids.title}
            className="border-t border-[var(--pv-border-subtle)] px-4 py-4 sm:px-5"
          >
            <p className="whitespace-pre-line text-[0.9375rem] leading-relaxed text-[var(--pv-color-text)] [overflow-wrap:anywhere]">
              {ad.body}
            </p>

            <h4 className="mt-5 text-[0.9375rem] font-bold text-[var(--pv-color-text)]">{t('detail.repliesHeading')}</h4>
            {detailState === 'loading' && !detail ? (
              <p role="status" className="mt-2 flex items-center gap-2 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
                <Loader2
                  className="h-[1.125rem] w-[1.125rem] shrink-0 animate-spin text-[var(--pv-color-primary)] motion-reduce:animate-none"
                  aria-hidden="true"
                />
                {t('detail.loading')}
              </p>
            ) : detailState === 'error' ? (
              <InlineAlert
                className="mt-2"
                action={
                  <Button variant="secondary" size="sm" onClick={() => void loadDetail()}>
                    <RefreshCw className="h-4 w-4" aria-hidden="true" />
                    {t('errors.retry')}
                  </Button>
                }
              >
                {t('detail.loadError')}
              </InlineAlert>
            ) : replies.length > 0 ? (
              <ul className="mt-2 space-y-2">
                {replies.map((r) => (
                  <li
                    key={r.id}
                    className="rounded-xl border border-[var(--pv-border-subtle)] bg-[var(--pv-color-surface-soft)] px-3.5 py-2.5 text-[0.9375rem] leading-relaxed text-[var(--pv-color-text)]"
                  >
                    {currentUserId !== null && r.userId === currentUserId ? (
                      <span className="mb-1 block text-[0.875rem] font-bold text-[var(--pv-color-primary)]">
                        {t('detail.yourReply')}
                      </span>
                    ) : null}
                    <span className="block whitespace-pre-line [overflow-wrap:anywhere]">{r.message}</span>
                  </li>
                ))}
              </ul>
            ) : detailState === 'ready' ? (
              <p className="mt-2 text-[0.9375rem] text-[var(--pv-color-text-muted)]">{t('detail.noReplies')}</p>
            ) : null}

            {token ? (
              <form noValidate onSubmit={submitReply} className="mt-4">
                <label htmlFor={ids.reply} className="mb-1.5 block text-[0.875rem] font-medium text-[var(--pv-color-text)]">
                  {t('detail.replyLabel')}
                </label>
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <Input
                      id={ids.reply}
                      value={reply}
                      onChange={(event) => {
                        setReply(event.target.value);
                        if (replyError) setReplyError(null);
                      }}
                      maxLength={2000}
                      autoComplete="off"
                      placeholder={t('detail.replyPlaceholder')}
                      invalid={replyError !== null}
                      aria-invalid={replyError !== null || undefined}
                      aria-describedby={replyError ? ids.replyError : undefined}
                    />
                  </div>
                  <Button type="submit" disabled={!reply.trim()} loading={replyBusy} className="shrink-0">
                    {!replyBusy ? <Send className="h-5 w-5 shrink-0 rtl:-scale-x-100" aria-hidden="true" /> : null}
                    {t('detail.send')}
                  </Button>
                </div>
                {replyError ? (
                  <p
                    id={ids.replyError}
                    role="alert"
                    className="mt-1.5 text-[0.875rem] leading-relaxed text-[#C62828] dark:text-[var(--pv-color-red)]"
                  >
                    {t(replyError)}
                  </p>
                ) : null}
              </form>
            ) : null}

            {isOwner ? (
              <div className="mt-5 border-t border-[var(--pv-border-subtle)] pt-4">
                {confirmingClose ? (
                  <div
                    role="group"
                    aria-labelledby={ids.confirm}
                    className="rounded-xl border border-[#DF2F2B]/25 bg-[var(--pv-color-red-soft)] p-4 dark:border-[#FF8A80]/30"
                  >
                    <p
                      id={ids.confirm}
                      ref={confirmTextRef}
                      tabIndex={-1}
                      className="text-[0.9375rem] leading-relaxed text-[var(--pv-color-text)] focus:outline-none"
                    >
                      {t('detail.closeConfirm')}
                    </p>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                      <Button variant="danger" size="sm" loading={closeBusy} onClick={() => void confirmClose()}>
                        {t('detail.closeYes')}
                      </Button>
                      <Button variant="secondary" size="sm" disabled={closeBusy} onClick={cancelClose}>
                        {t('detail.closeNo')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    ref={closeTriggerRef}
                    type="button"
                    onClick={startClose}
                    className="inline-flex min-h-[2.75rem] items-center gap-2 rounded-xl px-3 text-[0.9375rem] font-bold text-[#C62828] transition-colors duration-200 hover:bg-[var(--pv-color-red-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C62828]/30 dark:text-[var(--pv-color-red)]"
                  >
                    <XCircle className="h-[1.125rem] w-[1.125rem] shrink-0" aria-hidden="true" />
                    {t('detail.closePost')}
                  </button>
                )}
                {closeError ? (
                  <InlineAlert live="alert" className="mt-3">
                    {t(closeError)}
                  </InlineAlert>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </article>
    </li>
  );
}
